import { zodResolver } from "@hookform/resolvers/zod";
import { addMonths } from "date-fns";
import { type UseFormReturn, useForm } from "react-hook-form";
import { type CreateVotingPeriodFormData, createVotingPeriodSchema } from "../schemas/createVotingPeriod.schema";

type FormReturn = UseFormReturn<CreateVotingPeriodFormData>;

type UseCreateVotingPeriodFormReturn = {
    // Watched values
    candidateBookIds: string[];
    endsAt: Date | undefined;
    startsAt: Date | undefined;
    targetMonth: number;
    targetYear: number;

    // Form methods
    handleSubmit: FormReturn["handleSubmit"];

    // Custom handlers
    handleCandidateBooksChange: (bookIds: string[]) => void;
    handleEndsAtChange: (date: Date) => void;
    handleStartsAtChange: (date: Date) => void;
    handleTargetMonthYearChange: (year: number, month: number) => void;

    // Computed values
    isFormValid: boolean;
};

/**
 * Custom hook to manage create voting period form state using react-hook-form
 * Handles candidate book selection, date selection, and target month/year
 */
export function useCreateVotingPeriodForm(): UseCreateVotingPeriodFormReturn {
    // Calculate one month in advance, handling year rollover correctly
    const nextMonth = addMonths(new Date(), 1);
    const initialYear = nextMonth.getFullYear();
    const initialMonth = nextMonth.getMonth() + 1; // getMonth() returns 0-11, we need 1-12

    const {
        formState: { isValid },
        handleSubmit: rhfHandleSubmit,
        setValue,
        watch,
    } = useForm<CreateVotingPeriodFormData>({
        defaultValues: {
            candidateBookIds: [],
            endsAt: undefined,
            startsAt: undefined,
            targetMonth: initialMonth,
            targetYear: initialYear,
        },
        mode: "onChange",
        resolver: zodResolver(createVotingPeriodSchema),
    });

    const candidateBookIds = watch("candidateBookIds");
    const startsAt = watch("startsAt");
    const endsAt = watch("endsAt");
    const targetMonth = watch("targetMonth");
    const targetYear = watch("targetYear");

    const handleCandidateBooksChange = (bookIds: string[]) => {
        setValue("candidateBookIds", bookIds, { shouldValidate: true });
    };

    const handleTargetMonthYearChange = (year: number, month: number) => {
        setValue("targetMonth", month, { shouldValidate: true });
        setValue("targetYear", year, { shouldValidate: true });
    };

    const handleStartsAtChange = (date: Date) => {
        setValue("startsAt", date, { shouldValidate: true });
        // Update minDate for endsAt if needed - clear endsAt if it's before the new start date
        if (endsAt && endsAt < date) {
            setValue("endsAt", date, { shouldValidate: true });
        }
    };

    const handleEndsAtChange = (date: Date) => {
        setValue("endsAt", date, { shouldValidate: true });
    };

    return {
        candidateBookIds,
        endsAt,
        handleCandidateBooksChange,
        handleEndsAtChange,
        handleStartsAtChange,
        handleSubmit: rhfHandleSubmit,
        handleTargetMonthYearChange,
        isFormValid: isValid,
        startsAt,
        targetMonth,
        targetYear,
    };
}
